<template>
  <div class="resources-page">
    <Navbar :currentLang="currentLang" @change-lang="changeLang" />

    <!-- Hero -->
    <section class="resources-hero">
      <div class="resources-hero-inner">
        <span class="section-eyebrow" data-aos="fade-up">Resources</span>
        <h1 class="resources-hero-title" data-aos="fade-up" data-aos-delay="60">Guides, Tools &amp; Insights<br>to Grow Your Business</h1>
        <p class="resources-hero-sub" data-aos="fade-up" data-aos-delay="120">
          Everything you need to succeed with dropshipping and e-commerce fulfillment. From beginners to pros.
        </p>
        <!-- Search Bar -->
        <div class="resources-search" data-aos="fade-up" data-aos-delay="180">
          <svg class="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input type="text" placeholder="Search guides, tutorials, FAQs..." class="search-input">
          <button class="search-btn">Search</button>
        </div>
      </div>
    </section>

    <!-- Quick Links -->
    <section class="quick-links">
      <div class="quick-links-inner" data-aos="fade-up">
        <a href="#" class="quick-link" v-for="(ql, i) in quickLinks" :key="i">
          <div class="quick-link-icon" :style="{ background: ql.bg }">
            <i :class="ql.icon"></i>
          </div>
          <span class="quick-link-label">{{ ql.label }}</span>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="quick-link-arrow"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
        </a>
      </div>
    </section>

    <!-- Featured Guides (with real images) -->
    <section class="guides-section">
      <div class="guides-inner">
        <div class="section-header" data-aos="fade-up">
          <span class="section-eyebrow">Popular Guides</span>
          <h2 class="section-title">Start Learning</h2>
          <p class="section-desc">Step-by-step guides to help you launch and scale your e-commerce business.</p>
        </div>
        <div class="guides-grid">
          <a href="#" class="guide-card" v-for="(g, i) in guides" :key="i" data-aos="fade-up" :data-aos-delay="i * 80">
            <div class="guide-card-img" :style="{ background: g.bg }">
              <img :src="g.img" :alt="g.title" v-if="g.img" />
              <div class="guide-card-category">{{ g.category }}</div>
            </div>
            <div class="guide-card-body">
              <h3 class="guide-card-title">{{ g.title }}</h3>
              <p class="guide-card-desc">{{ g.desc }}</p>
              <div class="guide-card-meta">
                <span><i class="fa-regular fa-clock"></i> {{ g.readTime }}</span>
                <span>{{ g.date }}</span>
              </div>
            </div>
          </a>
        </div>
        <div class="guides-cta" data-aos="fade-up">
          <button class="btn-outline">View All Guides</button>
        </div>
      </div>
    </section>

    <!-- Video Tutorials (with real thumbnails) -->
    <section class="videos-section">
      <div class="videos-inner">
        <div class="section-header" data-aos="fade-up">
          <span class="section-eyebrow">Video Tutorials</span>
          <h2 class="section-title">Watch &amp; Learn</h2>
          <p class="section-desc">Short, practical videos showing you how to use FULFILLDROP effectively.</p>
        </div>
        <div class="videos-grid">
          <a href="#" class="video-card" v-for="(v, i) in videos" :key="i" data-aos="fade-up" :data-aos-delay="i * 80">
            <div class="video-thumb">
              <img :src="v.img" :alt="v.title" v-if="v.img" />
              <div class="video-play-btn">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
              </div>
              <span class="video-duration">{{ v.duration }}</span>
            </div>
            <div class="video-body">
              <h4 class="video-title">{{ v.title }}</h4>
              <p class="video-meta">{{ v.views }} views · {{ v.date }}</p>
            </div>
          </a>
        </div>
      </div>
    </section>

    <!-- Case Studies (with real images) -->
    <section class="cases-section">
      <div class="cases-inner">
        <div class="section-header" data-aos="fade-up">
          <span class="section-eyebrow">Case Studies</span>
          <h2 class="section-title">Success Stories</h2>
          <p class="section-desc">See how real sellers scaled their business with FULFILLDROP.</p>
        </div>
        <div class="cases-grid">
          <div class="case-card" v-for="(c, i) in caseStudies" :key="i" data-aos="fade-up" :data-aos-delay="i * 80">
            <div class="case-card-img">
              <img :src="c.img" :alt="c.title" v-if="c.img" />
              <div class="case-stats-overlay">
                <div class="case-stat" v-for="(s, j) in c.stats" :key="j">
                  <span class="case-stat-num">{{ s.num }}</span>
                  <span class="case-stat-label">{{ s.label }}</span>
                </div>
              </div>
            </div>
            <div class="case-card-body">
              <div class="case-tag">{{ c.tag }}</div>
              <h4 class="case-title">{{ c.title }}</h4>
              <p class="case-desc">{{ c.desc }}</p>
              <a href="#" class="case-link">Read Case Study →</a>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Downloads / Ebooks -->
    <section class="downloads-section">
      <div class="downloads-inner">
        <div class="section-header" data-aos="fade-up">
          <span class="section-eyebrow">Free Resources</span>
          <h2 class="section-title">Downloadable Tools</h2>
          <p class="section-desc">Free ebooks, checklists, and templates to help you succeed.</p>
        </div>
        <div class="downloads-grid" data-aos="fade-up" data-aos-delay="100">
          <div class="download-card" v-for="(d, i) in downloads" :key="i">
            <div class="download-icon" :style="{ background: d.bg }">
              <i :class="d.icon"></i>
            </div>
            <div class="download-body">
              <h4 class="download-title">{{ d.title }}</h4>
              <p class="download-desc">{{ d.desc }}</p>
              <span class="download-format">{{ d.format }}</span>
            </div>
            <button class="download-btn">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="width:18px;height:18px"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
              Download
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ Teaser -->
    <section class="faq-teaser">
      <div class="faq-teaser-inner" data-aos="fade-up">
        <div class="faq-teaser-text">
          <h2>Still Have Questions?</h2>
          <p>Our help center has answers to 100+ common questions. Can't find what you need? Talk to our team.</p>
          <div class="faq-teaser-btns">
            <router-link to="/faq" class="btn-primary">Visit Help Center</router-link>
            <router-link to="/contact" class="btn-ghost">Contact Support</router-link>
          </div>
        </div>
        <div class="faq-teaser-topics">
          <a href="#" class="faq-topic" v-for="(ft, i) in faqTopics" :key="i">
            <i :class="ft.icon"></i>
            <span>{{ ft.label }}</span>
          </a>
        </div>
      </div>
    </section>

    <Footer :currentLang="currentLang" />
  </div>
</template>

<script>
import Navbar from '../components/Navbar.vue'
import Footer from '../components/Footer.vue'

export default {
  name: 'Resources',
  components: { Navbar, Footer },
  data() {
    return {
      currentLang: 'en',
      quickLinks: [
        { label: 'Help Center', icon: 'fa-solid fa-circle-question', bg: '#e6f7f9' },
        { label: 'API Docs', icon: 'fa-solid fa-code', bg: '#f0fdf4' },
        { label: 'Blog', icon: 'fa-solid fa-pen-nib', bg: '#fef3c7' },
        { label: 'Community', icon: 'fa-solid fa-users', bg: '#fce7f3' },
      ],
      guides: [
        { title: 'How to Start Dropshipping in 2024', desc: 'A complete beginner\'s guide to launching your first dropshipping store.', category: 'GUIDE', readTime: '12 min read', date: 'Jan 15, 2024', bg: '', img: 'https://static.wixstatic.com/media/575a2a_a46e4c3252d346789313e3480eae0081~mv2.png/v1/fill/w_400,h_250,al_c,q_85,enc_avif,quality_auto/575a2a_a46e4c3252d346789313e3480eae0081~mv2.png' },
        { title: '10 Must-Have Shopify Apps for Scaling', desc: 'The essential apps every growing Shopify store needs.', category: 'TOOLS', readTime: '8 min read', date: 'Feb 3, 2024', bg: '', img: 'https://static.wixstatic.com/media/575a2a_812d252af6ea443aba66d66751eae5f0~mv2.png/v1/fill/w_400,h_250,al_c,q_85,enc_avif,quality_auto/575a2a_812d252af6ea443aba66d66751eae5f0~mv2.png' },
        { title: 'China Sourcing: How to Find Reliable Suppliers', desc: 'Proven strategies to vet and build relationships with Chinese suppliers.', category: 'SOURCING', readTime: '15 min read', date: 'Mar 12, 2024', bg: '', img: 'https://static.wixstatic.com/media/575a2a_9661c470e6c64d31bc14fb2946703ea2~mv2.png/v1/fill/w_400,h_250,al_c,q_85,enc_avif,quality_auto/575a2a_9661c470e6c64d31bc14fb2946703ea2~mv2.png' },
        { title: 'FBA vs FBM: Which is Right for You?', desc: 'A detailed comparison to help you choose the best fulfillment strategy.', category: 'FULFILLMENT', readTime: '10 min read', date: 'Apr 8, 2024', bg: '', img: 'https://static.wixstatic.com/media/575a2a_67257af414b14ce4a7941062339a4bc2f000.jpg/v1/fill/w_400,h_250,al_c,q_85,enc_avif,quality_auto/575a2a_67257af414b14ce4a7941062339a4bc2f000.jpg' },
        { title: 'How to Reduce Returns by 40%', desc: 'Actionable QC and packaging strategies that actually work.', category: 'QC', readTime: '9 min read', date: 'May 20, 2024', bg: '', img: 'https://static.wixstatic.com/media/575a2a_83df7795f2f64b4db8fd052e30d61870~mv2.png/v1/fill/w_400,h_250,al_c,q_85,enc_avif,quality_auto/3.png' },
        { title: 'Understanding Shipping Times by Country', desc: 'A comprehensive guide to international shipping timelines.', category: 'LOGISTICS', readTime: '11 min read', date: 'Jun 5, 2024', bg: '', img: 'https://static.wixstatic.com/media/575a2a_f2ac3c53f08d4322a3a7dc238155d975~mv2.png/v1/fill/w_400,h_250,al_c,q_85,enc_avif,quality_auto/ChatGPT%20Image%202026%20January%2030%20at%2017_05_41.png' },
      ],
      videos: [
        { title: 'Dashboard Overview – 2024 Update', duration: '4:32', views: '12.4K', date: 'Mar 2024', bg: '', img: 'https://static.wixstatic.com/media/575a2a_3f47341d16ca4f4b9e2f8ed488e08070~mv2.jpg/v1/fit/w_400,h_250,q_90,enc_avif,quality_auto/575a2a_3f47341d16ca4f4b9e2f8ed488e08070~mv2.jpg' },
        { title: 'How to Connect Your Shopify Store', duration: '6:15', views: '8.7K', date: 'Apr 2024', bg: '', img: 'https://static.wixstatic.com/media/575a2a_86bb72572ccc4412b795107a130ee0e2~mv2.jpg/v1/fit/w_400,h_250,q_90,enc_avif,quality_auto/575a2a_86bb72572ccc4412b795107a130ee0e2~mv2.jpg' },
        { title: 'Setting Up Branded Packaging', duration: '5:48', views: '5.2K', date: 'May 2024', bg: '', img: 'https://static.wixstatic.com/media/575a2a_693bdababfcb4ea88dabff857ec15372~mv2.jpg/v1/fit/w_400,h_250,q_90,enc_avif,quality_auto/575a2a_693bdababfcb4ea88dabff857ec15372~mv2.jpg' },
      ],
      caseStudies: [
        { title: 'How EcoWare Grew from $0 to $2M in 12 Months', desc: 'Learn how this sustainable home goods brand scaled rapidly with FULFILLDROP\'s fulfillment network.', tag: 'CASE STUDY', bg: '', img: 'https://static.wixstatic.com/media/575a2a_83df7795f2f64b4db8fd052e30d61870~mv2.png/v1/fill/w_400,h_250,al_c,q_85,enc_avif,quality_auto/3.png', stats: [{ num: '$2M+', label: 'Revenue' }, { num: '12', label: 'Months' }] },
        { title: 'PetPals: Reducing Shipping Costs by 35%', desc: 'How strategic warehouse placement cut delivery costs and improved customer satisfaction.', tag: 'LOGISTICS', bg: '', img: 'https://static.wixstatic.com/media/575a2a_67257af414b14ce4a7941062339a4bc2f000.jpg/v1/fill/w_400,h_250,al_c,q_85,enc_avif,quality_auto/575a2a_67257af414b14ce4a7941062339a4bc2f000.jpg', stats: [{ num: '35%', label: 'Cost Saved' }, { num: '2.1K', label: 'Orders/mo' }] },
      ],
      downloads: [
        { title: 'The Ultimate Dropshipping Checklist', desc: '50-point checklist before launching your store.', format: 'PDF · 12 pages', icon: 'fa-solid fa-file-pdf', bg: '#fee2e2' },
        { title: 'Supplier Vetting Template', desc: 'Excel template to evaluate and compare suppliers.', format: 'XLSX · 3 sheets', icon: 'fa-solid fa-file-excel', bg: '#dcfce7' },
        { title: 'Shipping Rate Calculator', desc: 'Calculate total landed cost for any destination.', format: 'XLSX · 1 sheet', icon: 'fa-solid fa-file-excel', bg: '#dbeafe' },
        { title: 'E-commerce KPI Dashboard', desc: 'Track the metrics that actually matter.', format: 'XLSX · 5 sheets', icon: 'fa-solid fa-file-excel', bg: '#fef3c7' },
      ],
      faqTopics: [
        { label: 'Orders & Tracking', icon: 'fa-solid fa-truck' },
        { label: 'Payments & Pricing', icon: 'fa-solid fa-credit-card' },
        { label: 'Account & Settings', icon: 'fa-solid fa-gear' },
        { label: 'Integrations', icon: 'fa-solid fa-plug' },
        { label: 'Shipping & Delivery', icon: 'fa-solid fa-plane' },
        { label: 'Returns & Refunds', icon: 'fa-solid fa-rotate-left' },
      ]
    }
  },
  methods: {
    changeLang(lang) {
      this.currentLang = lang
    }
  }
}
</script>

<style scoped>
.resources-page {
  font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;
}

/* Hero */
.resources-hero {
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  padding: 100px 2rem 50px;
  text-align: center;
  color: #fff;
}
.resources-hero-inner {
  max-width: 700px;
  margin: 0 auto;
}
.section-eyebrow {
  display: inline-block;
  background: rgba(40,181,193,0.25);
  color: #a5b4fc;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  padding: 5px 14px;
  border-radius: 20px;
  margin-bottom: 16px;
}
.resources-hero-title {
  font-size: 2.8rem;
  font-weight: 800;
  line-height: 1.15;
  margin: 0 0 16px;
}
.resources-hero-sub {
  font-size: 1.05rem;
  opacity: 0.82;
  line-height: 1.6;
  margin-bottom: 28px;
}

/* Search */
.resources-search {
  display: flex;
  align-items: center;
  background: rgba(255,255,255,0.1);
  border: 1.5px solid rgba(255,255,255,0.2);
  border-radius: 14px;
  padding: 6px 6px 6px 16px;
  max-width: 480px;
  margin: 0 auto;
}
.search-icon {
  width: 18px;
  height: 18px;
  color: #94a3b8;
  flex-shrink: 0;
}
.search-input {
  flex: 1;
  background: none;
  border: none;
  color: #fff;
  font-size: 0.92rem;
  padding: 10px;
  outline: none;
}
.search-input::placeholder { color: #64748b; }
.search-btn {
  background: #28b5c1;
  color: #fff;
  border: none;
  padding: 10px 20px;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}
.search-btn:hover { background: #3ec4cf; }

/* Quick Links */
.quick-links {
  background: #fff;
  margin-top: -30px;
  position: relative;
  z-index: 2;
}
.quick-links-inner {
  max-width: 900px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  padding: 0 2rem;
}
.quick-link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  text-decoration: none;
  transition: transform 0.2s, box-shadow 0.2s;
}
.quick-link:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 28px rgba(0,0,0,0.12);
}
.quick-link-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  color: #28b5c1;
  flex-shrink: 0;
}
.quick-link-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #1e293b;
  flex: 1;
}
.quick-link-arrow {
  width: 16px;
  height: 16px;
  color: #94a3b8;
}

/* Guides */
.guides-section {
  padding: 70px 2rem 50px;
  background: #f8fafc;
}
.guides-inner {
  max-width: 1100px;
  margin: 0 auto;
}
.section-header {
  text-align: center;
  margin-bottom: 40px;
}
.section-title {
  font-size: 1.9rem;
  font-weight: 800;
  color: #1e293b;
  margin: 12px 0 14px;
}
.section-desc {
  font-size: 0.95rem;
  color: #64748b;
  max-width: 520px;
  margin: 0 auto;
  line-height: 1.6;
}
.guides-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}
.guide-card {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
  text-decoration: none;
  transition: transform 0.2s, box-shadow 0.2s;
  border: 1px solid #f1f5f9;
}
.guide-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 36px rgba(0,0,0,0.1);
}
.guide-card-img {
  height: 180px;
  position: relative;
  overflow: hidden;
}
.guide-card-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.35s;
}
.guide-card:hover .guide-card-img img {
  transform: scale(1.06);
}
.guide-card-category {
  position: absolute;
  bottom: 10px;
  left: 10px;
  background: rgba(0,0,0,0.55);
  backdrop-filter: blur(4px);
  color: #fff;
  font-size: 0.68rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 6px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  z-index: 2;
}
.guide-card-body {
  padding: 18px;
}
.guide-card-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px;
  line-height: 1.4;
}
.guide-card-desc {
  font-size: 0.82rem;
  color: #64748b;
  line-height: 1.5;
  margin: 0 0 12px;
}
.guide-card-meta {
  display: flex;
  gap: 14px;
  font-size: 0.75rem;
  color: #94a3b8;
}
.guide-card-meta i { margin-right: 4px; }
.guides-cta {
  text-align: center;
  margin-top: 32px;
}
.btn-outline {
  background: #fff;
  color: #28b5c1;
  padding: 11px 24px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  text-decoration: none;
  display: inline-block;
}
.btn-outline:hover {
  border-color: #28b5c1;
  background: #f8f7ff;
}

/* Videos */
.videos-section {
  padding: 60px 2rem;
  background: #fff;
}
.videos-inner {
  max-width: 1100px;
  margin: 0 auto;
  text-align: center;
}
.videos-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  text-align: left;
}
.video-card {
  text-decoration: none;
  transition: transform 0.2s;
}
.video-card:hover { transform: translateY(-4px); }
.video-thumb {
  height: 170px;
  position: relative;
  overflow: hidden;
}
.video-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.video-play-btn {
  width: 50px;
  height: 50px;
  background: rgba(0,0,0,0.5);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.2rem;
  transition: background 0.2s, transform 0.2s;
}
.video-card:hover .video-play-btn {
  background: #28b5c1;
  transform: scale(1.1);
}
.video-play-btn svg { margin-left: 3px; }
.video-duration {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(0,0,0,0.7);
  color: #fff;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 4px;
}
.video-body {
  padding: 12px 4px;
}
.video-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 6px;
  line-height: 1.4;
}
.video-meta {
  font-size: 0.78rem;
  color: #94a3b8;
  margin: 0;
}

/* Cases */
.cases-section {
  padding: 60px 2rem;
  background: #f8fafc;
}
.cases-inner {
  max-width: 1100px;
  margin: 0 auto;
  text-align: center;
}
.cases-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  text-align: left;
}
.case-card {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
  border: 1px solid #f1f5f9;
  transition: transform 0.2s, box-shadow 0.2s;
}
.case-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 36px rgba(0,0,0,0.1);
}
.case-card-img {
  height: 160px;
  position: relative;
  overflow: hidden;
}
.case-card-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.case-stats-overlay {
  display: flex;
  gap: 16px;
  padding: 16px;
  width: 100%;
}
.case-stat {
  background: rgba(0,0,0,0.5);
  backdrop-filter: blur(4px);
  border-radius: 10px;
  padding: 10px 14px;
  flex: 1;
  text-align: center;
}
.case-stat-num {
  display: block;
  font-size: 1.1rem;
  font-weight: 800;
  color: #fff;
}
.case-stat-label {
  font-size: 0.68rem;
  color: rgba(255,255,255,0.8);
  font-weight: 500;
}
.case-card-body {
  padding: 18px;
}
.case-tag {
  display: inline-block;
  background: #e6f7f9;
  color: #28b5c1;
  font-size: 0.68rem;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 6px;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.case-title {
  font-size: 1rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px;
  line-height: 1.4;
}
.case-desc {
  font-size: 0.82rem;
  color: #64748b;
  line-height: 1.5;
  margin: 0 0 12px;
}
.case-link {
  font-size: 0.85rem;
  font-weight: 600;
  color: #28b5c1;
  text-decoration: none;
}
.case-link:hover { text-decoration: underline; }

/* Downloads */
.downloads-section {
  padding: 60px 2rem;
  background: #fff;
}
.downloads-inner {
  max-width: 900px;
  margin: 0 auto;
  text-align: center;
}
.downloads-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  text-align: left;
}
.download-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px;
  background: #f8fafc;
  border-radius: 14px;
  border: 1px solid #f1f5f9;
  transition: box-shadow 0.2s;
}
.download-card:hover {
  box-shadow: 0 4px 16px rgba(0,0,0,0.06);
}
.download-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  color: #dc2626;
  flex-shrink: 0;
}
.download-body {
  flex: 1;
  min-width: 0;
}
.download-title {
  font-size: 0.88rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px;
}
.download-desc {
  font-size: 0.78rem;
  color: #64748b;
  margin: 0 0 4px;
}
.download-format {
  font-size: 0.7rem;
  color: #94a3b8;
  font-weight: 500;
}
.download-btn {
  background: #28b5c1;
  color: #fff;
  border: none;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  transition: background 0.2s;
}
.download-btn:hover { background: #3ec4cf; }

/* FAQ Teaser */
.faq-teaser {
  padding: 60px 2rem;
  background: #0f172a;
  color: #fff;
}
.faq-teaser-inner {
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  align-items: center;
}
.faq-teaser-text h2 {
  font-size: 1.8rem;
  font-weight: 800;
  margin: 0 0 12px;
}
.faq-teaser-text p {
  font-size: 0.95rem;
  color: #94a3b8;
  line-height: 1.6;
  margin: 0 0 24px;
}
.faq-teaser-btns {
  display: flex;
  gap: 12px;
}
.btn-primary {
  background: linear-gradient(135deg, #28b5c1, #1e9aa5);
  color: #fff;
  padding: 12px 24px;
  border: none;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  text-decoration: none;
  display: inline-block;
  transition: transform 0.15s, box-shadow 0.15s;
}
.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(40,181,193,0.4);
}
.btn-ghost {
  background: rgba(255,255,255,0.1);
  color: #fff;
  padding: 12px 24px;
  border: 1.5px solid rgba(255,255,255,0.25);
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  text-decoration: none;
  display: inline-block;
  transition: background 0.2s;
}
.btn-ghost:hover { background: rgba(255,255,255,0.18); }
.faq-teaser-topics {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}
.faq-topic {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: rgba(255,255,255,0.06);
  border-radius: 10px;
  color: #cbd5e1;
  text-decoration: none;
  font-size: 0.85rem;
  font-weight: 500;
  transition: background 0.2s, color 0.2s;
}
.faq-topic:hover {
  background: rgba(255,255,255,0.12);
  color: #fff;
}
.faq-topic i {
  font-size: 1rem;
  color: #28b5c1;
}

/* Responsive */
@media (max-width: 1023px) {
  .quick-links-inner { grid-template-columns: repeat(2, 1fr); }
  .guides-grid { grid-template-columns: repeat(2, 1fr); }
  .videos-grid { grid-template-columns: repeat(2, 1fr); }
  .cases-grid { grid-template-columns: 1fr; }
  .faq-teaser-inner { grid-template-columns: 1fr; }
}
@media (max-width: 640px) {
  .resources-hero-title { font-size: 2rem; }
  .quick-links-inner { grid-template-columns: 1fr; }
  .guides-grid { grid-template-columns: 1fr; }
  .videos-grid { grid-template-columns: 1fr; }
  .downloads-grid { grid-template-columns: 1fr; }
  .faq-teaser-topics { grid-template-columns: 1fr; }
}
</style>
